import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('bt_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('bt_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('bt_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Try to link any pending subscription from checkout
    const pendingSessionId = sessionStorage.getItem('bt_checkout_session');
    if (pendingSessionId) {
      try {
        await axios.post(`${API_URL}/api/auth/link-subscription`, 
          { session_id: pendingSessionId },
          { headers: { Authorization: `Bearer ${access_token}` }}
        );
        sessionStorage.removeItem('bt_checkout_session');
      } catch (e) {
        console.log('No subscription to link');
      }
    }
    
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      name
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('bt_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Try to link any pending subscription from checkout
    const pendingSessionId = sessionStorage.getItem('bt_checkout_session');
    if (pendingSessionId) {
      try {
        await axios.post(`${API_URL}/api/auth/link-subscription`, 
          { session_id: pendingSessionId },
          { headers: { Authorization: `Bearer ${access_token}` }}
        );
        sessionStorage.removeItem('bt_checkout_session');
      } catch (e) {
        console.log('No subscription to link');
      }
    }
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('bt_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
